import { SupabaseClient } from '@supabase/supabase-js';
import { UserRepository } from "../repositories/user.repository";
import { UserMapper } from "../mappers/user.mapper";
import { UserDTO } from "../dtos/user.dto";

// Supported image formats
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export class UserService {
  private userRepository: UserRepository;

  constructor(private supabase: SupabaseClient) {
    this.userRepository = new UserRepository(supabase);
  }

  /**
   * Get user by ID
   */
  async getUser(userId: string): Promise<UserDTO | null> {
    try {
      const user = await this.userRepository.findById(userId);
      if (!user) {
        return null;
      }
      return UserMapper.toDTO(user);
    } catch (error) {
      throw new Error(
        `Failed to get user: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Upload user profile image
   * @param userId User ID
   * @param file File data as ArrayBuffer or Blob
   * @param fileName Original file name
   * @param mimeType MIME type of the file
   * @returns Updated user DTO with new avatar URL
   */
  async uploadProfileImage(
    userId: string,
    file: ArrayBuffer | Blob,
    fileName: string,
    mimeType: string
  ): Promise<UserDTO> {
    try {
      // Validate file type
      if (!ALLOWED_IMAGE_TYPES.includes(mimeType)) {
        throw new Error(`Invalid file type. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`);
      }

      // Validate file size
      const fileSize = file instanceof ArrayBuffer ? file.byteLength : file.size;
      if (fileSize > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds limit of ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      }

      // Get user
      const user = await this.userRepository.findById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Delete existing avatar if present
      if (user.avatar_url) {
        await this.deleteExistingAvatar(user.avatar_url);
      }

      // Generate unique file name with timestamp
      const fileExtension = fileName.split('.').pop() || 'jpg';
      const timestamp = Date.now();
      const uniqueFileName = `${userId}/${timestamp}.${fileExtension}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await this.supabase.storage
        .from('avatars')
        .upload(uniqueFileName, file, {
          contentType: mimeType,
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Failed to upload image: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = this.supabase.storage
        .from('avatars')
        .getPublicUrl(uniqueFileName);

      // Update user profile with new avatar URL
      user.updateProfile(user.full_name, publicUrl);
      const updatedUser = await this.userRepository.save(user);

      return UserMapper.toDTO(updatedUser);
    } catch (error) {
      throw new Error(
        `Failed to upload profile image: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Update user name
   * @param userId User ID
   * @param fullName Full name
   * @returns Updated user DTO
   */
  async updateUserName(userId: string, fullName: string | null): Promise<UserDTO> {
    try {
      const user = await this.userRepository.findById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Update user profile with existing avatar URL
      user.updateProfile(fullName, user.avatar_url);
      const updatedUser = await this.userRepository.save(user);

      return UserMapper.toDTO(updatedUser);
    } catch (error) {
      throw new Error(
        `Failed to update user name: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Delete user profile image
   * @param userId User ID
   * @returns Updated user DTO with null avatar URL
   */
  async deleteProfileImage(userId: string): Promise<UserDTO> {
    try {
      const user = await this.userRepository.findById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      if (!user.avatar_url) {
        // No avatar to delete
        return UserMapper.toDTO(user);
      }

      // Delete from storage
      await this.deleteExistingAvatar(user.avatar_url);

      // Update user profile
      user.updateProfile(user.full_name, null);
      const updatedUser = await this.userRepository.save(user);

      return UserMapper.toDTO(updatedUser);
    } catch (error) {
      throw new Error(
        `Failed to delete profile image: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Delete user account permanently
   * This will delete the user's profile, all associated data, and sign them out
   * @param userId User ID to delete
   * @returns Promise<void>
   */
  async deleteUserAccount(userId: string): Promise<void> {
    try {
      // Get current session
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the delete-account Edge Function
      const { data, error } = await this.supabase.functions.invoke('delete-account', {
        body: {
          reason: 'User initiated deletion'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to delete account');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (!data?.success) {
        throw new Error('Failed to delete account');
      }
    } catch (error) {
      throw new Error(
        `Failed to delete user account: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  /**
   * Delete all avatar files for a user from storage
   * @param userId User ID
   */
  private async deleteAllUserAvatars(userId: string): Promise<void> {
    try {
      // List all files in the user's avatar folder
      const { data: files, error: listError } = await this.supabase.storage
        .from('avatars')
        .list(userId);

      if (listError || !files || files.length === 0) {
        // No files to delete or error listing (non-critical)
        return;
      }

      // Delete all files in the user's folder
      const filePaths = files.map(file => `${userId}/${file.name}`);
      const { error: deleteError } = await this.supabase.storage
        .from('avatars')
        .remove(filePaths);

      if (deleteError && deleteError.message !== 'Object not found') {
        console.error('Failed to delete user avatar files:', deleteError);
      }
    } catch (error) {
      // Log error but don't fail the account deletion process
      console.error('Failed to cleanup user avatars:', error);
    }
  }

  /**
   * Delete existing avatar from storage
   * @param avatarUrl Current avatar URL
   */
  private async deleteExistingAvatar(avatarUrl: string): Promise<void> {
    try {
      // Extract file path from URL
      const url = new URL(avatarUrl);
      const pathParts = url.pathname.split('/');
      const bucketIndex = pathParts.indexOf('avatars');

      if (bucketIndex === -1) {
        // Not a storage URL or different bucket
        return;
      }

      const filePath = pathParts.slice(bucketIndex + 1).join('/');

      // Delete from storage
      const { error } = await this.supabase.storage
        .from('avatars')
        .remove([filePath]);

      if (error && error.message !== 'Object not found') {
        // Ignore "not found" errors, but throw others
        throw error;
      }
    } catch (error) {
      // Log error but don't fail the upload process
      console.error('Failed to delete existing avatar:', error);
    }
  }
}

